"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { cn } from "@/lib/utils";

/* Flagship mock for "99.9%+ Platform Uptime" — gateway availability over 24h: a
   bar strip where all but a few five-minute buckets were fully up, the live
   request log underneath, and the two numbers that matter.

   Deliberately the same shape as the homepage's UptimeMock (bar strip → rule →
   paired stats): that card already teaches the reader how to read this one.
   This is a platform/infrastructure claim (Proxidize's own gateway uptime),
   not a claim about request outcomes against external target sites — those
   depend on factors outside Proxidize's control. The bars grow on scroll-in
   on the same stagger.

   Token-based for both themes, fixed content so the height never moves.
   Decorative. */

const GREEN_TINT = "color-mix(in srgb, var(--brand-green) 12%, transparent)";
const FIELD_BG = "color-mix(in srgb, var(--foreground) 3%, transparent)";

/* 72 five-minute buckets. Two shallow dips and one deeper one, so the strip
   reads as measured data rather than a solid green wall — the claim is 99.9%,
   not 100%. */
const RETRY_DIPS = new Set([17, 43, 58]);
const DEEP_DIP = 43;

const BUCKETS = Array.from({ length: 72 }, (_, i) => ({
  id: `b${i}`,
  dip: RETRY_DIPS.has(i),
  deep: i === DEEP_DIP,
}));

const REQUEST_LOG = [
  { id: "q1", method: "GET", path: "/search?q=…", status: "200", ms: "412ms" },
  { id: "q2", method: "GET", path: "/products/4821", status: "200", ms: "298ms" },
  { id: "q3", method: "GET", path: "/reviews?page=2", status: "200", ms: "355ms" },
];

const FOOT_STATS = [
  { id: "ok", value: "99.94%", label: "Platform uptime" },
  { id: "retry", value: "0.06%", label: "Scheduled downtime" },
];

export default function SuccessRateMock() {
  const reduced = useReducedMotion();

  return (
    <div className="border-border bg-popover w-full rounded-xl border p-4" aria-hidden="true">
      <div className="flex items-center justify-between">
        <span
          className="flex items-center gap-1.5 text-[12px] font-medium"
          style={{ color: "var(--brand-green)" }}
        >
          <span className="relative flex h-2 w-2">
            {!reduced && (
              <span
                className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
                style={{ backgroundColor: "var(--brand-green)" }}
              />
            )}
            <span
              className="relative inline-flex h-2 w-2 rounded-full"
              style={{ backgroundColor: "var(--brand-green)" }}
            />
          </span>
          All systems operational
        </span>
        <span className="text-muted-foreground text-[11px]">Last 24 hours</span>
      </div>

      {/* Outcome strip — one bar per five-minute bucket */}
      <motion.div
        className="mt-4 flex h-14 items-end gap-0.5"
        initial={reduced ? false : "hidden"}
        whileInView={reduced ? undefined : "visible"}
        viewport={scrollRevealViewport}
        variants={{ visible: { transition: { staggerChildren: 0.008 } } }}
      >
        {BUCKETS.map((bucket) => (
          <motion.div
            key={bucket.id}
            className={cn(
              "flex-1 origin-bottom rounded-sm",
              bucket.deep ? "h-2/5" : bucket.dip ? "h-3/5" : "h-full"
            )}
            style={{
              backgroundColor: bucket.dip
                ? "var(--amber-accent)"
                : "color-mix(in srgb, var(--brand-green) 55%, transparent)",
            }}
            variants={reduced ? undefined : { hidden: { scaleY: 0.12 }, visible: { scaleY: 1 } }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
        ))}
      </motion.div>

      {/* Live log — what those bars are made of */}
      <div className="mt-4 space-y-1">
        {REQUEST_LOG.map((req) => (
          <div
            key={req.id}
            className="border-border flex items-center gap-2 rounded-md border px-2 py-1.5"
            style={{ backgroundColor: FIELD_BG }}
          >
            <span className="text-muted-foreground shrink-0 font-mono text-[9px] font-semibold">
              {req.method}
            </span>
            <span className="text-foreground min-w-0 flex-1 truncate font-mono text-[10px]">
              {req.path}
            </span>
            <span className="text-muted-foreground shrink-0 font-mono text-[9px]">{req.ms}</span>
            <span
              className="shrink-0 rounded px-1.5 py-px font-mono text-[9px] font-semibold"
              style={{ backgroundColor: GREEN_TINT, color: "var(--brand-green)" }}
            >
              {req.status}
            </span>
          </div>
        ))}
      </div>

      <div className="border-border mt-4 flex items-center justify-between border-t pt-4">
        {FOOT_STATS.map((stat) => (
          <div key={stat.id} className="last:text-right">
            <div className="text-foreground text-2xl font-semibold tracking-tight">
              {stat.value}
            </div>
            <div className="text-muted-foreground text-[11px]">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
